import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRbac } from "../../context";
import {
  HotlineItem,
  HotlineJobOption,
  HotlinePerson,
  importHotlineAssignments,
  getHotlineAssignments,
  getHotlineHotLeadSettings,
  getHotlineJobs,
  getHotlineLiveSheetBranches,
  getHotlineTeam,
  removeHotlineItem,
  saveHotlineHotLeadRecruiter,
  saveHotlineHotLeadTeam,
  saveHotlineLiveSheetBranch,
} from "../../services/hotlineQueueApi";
import { SearchableSelect } from "../../components/ui/searchable-select";
import HotlineTracking, { CALL_STATUS_COLUMNS, HotlineAnalytics, HotlineLiveTracking } from "./tracking";

const ROLE_LABEL: Record<string, string> = {
  HR: "Intern Recruiter",
  HM: "Freelance Recruiter",
  SHM: "Senior Hiring Manager",
  SHM_ADMIN: "Senior Hiring Manager Admin",
  FBDE_ADMIN: "Franchise Manager Admin",
};

const STATUS_LABEL: Record<string, string> = {
  waiting: "Waiting",
  inProgress: "In progress",
  callbackScheduled: "Callback",
  completed: "Done",
  skipped: "Skipped",
};

const CALL_STATUS_LABEL: Record<string, string> = Object.fromEntries(CALL_STATUS_COLUMNS);

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  ...Object.values(STATUS_LABEL).map((label) => ({ value: label, label })),
  ...CALL_STATUS_COLUMNS.map(([, label]) => ({ value: label, label })),
];

function assignmentStatus(item: HotlineItem) {
  if (item.lastDisposition) {
    return CALL_STATUS_LABEL[item.lastDisposition] || item.lastDisposition;
  }
  return STATUS_LABEL[item.status] || item.status;
}

const SHEET_SOURCES = [
  { id: "apna", number: "3", title: "Apna", category: "apna" },
  { id: "jobhe", number: "4", title: "Jobhe", category: "jobhe" },
  { id: "workIndia", number: "5", title: "Work India", category: "workIndia" },
] as const;

function sourceLabel(category: string, sourceType?: string | null) {
  if (sourceType === "hotLead" || category === "hotLeads") return "Hot leads";
  const sheet = SHEET_SOURCES.find(
    (source) => source.id === sourceType || source.category === category,
  );
  return sheet?.title || "Excel";
}

type CsvPreviewRow = {
  candidateName: string;
  phone: string;
  ready: boolean;
  reason: string;
};

function parseQueueCsv(text: string) {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim());
  const cells = (line: string) => {
    const values: string[] = [];
    let current = "";
    let quoted = false;
    for (let index = 0; index < line.length; index += 1) {
      const char = line[index];
      if (char === '"') {
        if (quoted && line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          quoted = !quoted;
        }
      } else if (char === "," && !quoted) {
        values.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };
  if (!lines.length) return [];
  const header = cells(lines[0]).map((value) => value.toLowerCase());
  const nameIndex = header.findIndex((value) => value.includes("name"));
  const phoneIndex = header.findIndex((value) => /mobile|phone|number/.test(value));
  const hasHeader = nameIndex >= 0 && phoneIndex >= 0;
  return (hasHeader ? lines.slice(1) : lines)
    .map((line) => {
      const row = cells(line);
      return {
        candidateName: row[hasHeader ? nameIndex : 0] || "",
        phone: row[hasHeader ? phoneIndex : 1] || "",
      };
    })
    .filter((row) => row.candidateName || row.phone);
}

function errorMessage(error: unknown) {
  const body = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return body?.message || "The Hotline request could not be completed.";
}

const HIERARCHY_PREVIEW = 20;

function branchIds(people: HotlinePerson[], rootId: string) {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const person of people) {
      if (person.managerId && ids.has(person.managerId) && !ids.has(person.id)) {
        ids.add(person.id);
        grew = true;
      }
    }
  }
  return ids;
}

const HotlinePage: React.FC = () => {
  const { user } = useRbac();
  const [people, setPeople] = useState<HotlinePerson[]>([]);
  const [hierarchyPeople, setHierarchyPeople] = useState<HotlinePerson[]>([]);
  const [hierarchyTotal, setHierarchyTotal] = useState(0);
  const [items, setItems] = useState<HotlineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>("");
  const [hierarchyQuery, setHierarchyQuery] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvRows, setCsvRows] = useState<CsvPreviewRow[]>([]);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [jobs, setJobs] = useState<HotlineJobOption[]>([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobSearch, setJobSearch] = useState("");
  const [jobsLoading, setJobsLoading] = useState(false);
  const [pickedJob, setPickedJob] = useState<HotlineJobOption | null>(null);
  const [form, setForm] = useState({ jobId: "" });
  const [panel, setPanel] = useState<"assign" | "live" | "track" | "analytics">("assign");
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [hotLeadsOn, setHotLeadsOn] = useState(false);
  const [hotLeadSettings, setHotLeadSettings] = useState<
    Record<string, { enabled: boolean; max: string; passedCount: number }>
  >({});
  const canEditHotLeads = ["ADMIN", "HM", "SHM", "SHM_ADMIN", "FBDE_ADMIN"].includes(
    user?.role ?? "",
  );
  const showExcel = user?.role !== "HR";
  const showSourceTools = !["HR", "HM"].includes(user?.role ?? "");
  const showTracking = user?.role !== "HR";
  const [liveSheets, setLiveSheets] = useState<Record<string, { enabled: boolean }>>({});

  const load = async () => {
    setLoading(true);
    try {
      const [team, preview, rows] = await Promise.all([
        getHotlineTeam(),
        getHotlineTeam({ limit: HIERARCHY_PREVIEW }),
        getHotlineAssignments(),
      ]);
      setPeople(team.people);
      setHierarchyPeople(preview.people);
      setHierarchyTotal(preview.total);
      setItems(rows);
      try {
        const result = await getHotlineJobs();
        setJobs(result.jobs);
        setJobsTotal(result.total);
      } catch {
        setJobs([]);
        setJobsTotal(0);
      }
      setSelectedId((current) => {
        if (current && team.people.some((person) => person.id === current)) return current;
        if (user?.id && team.people.some((person) => person.id === user.id)) return user.id;
        return preview.people[0]?.id || team.people[0]?.id || "";
      });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // The signed-in account is stable for this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skipHierarchyFetch = useRef(true);
  useEffect(() => {
    if (skipHierarchyFetch.current) {
      skipHierarchyFetch.current = false;
      return;
    }
    const query = hierarchyQuery.trim();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      getHotlineTeam({ limit: HIERARCHY_PREVIEW, search: query || undefined })
        .then((result) => {
          if (cancelled) return;
          setHierarchyPeople(result.people);
          setHierarchyTotal(result.total);
        })
        .catch((error) => {
          if (!cancelled) toast.error(errorMessage(error));
        });
    }, query ? 300 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hierarchyQuery]);

  const skipJobSearch = useRef(true);
  useEffect(() => {
    if (skipJobSearch.current) {
      skipJobSearch.current = false;
      return;
    }
    const query = jobSearch.trim();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setJobsLoading(true);
      getHotlineJobs({ search: query || undefined })
        .then((result) => {
          if (cancelled) return;
          setJobs(result.jobs);
          setJobsTotal(result.total);
        })
        .catch((error) => {
          if (!cancelled) toast.error(errorMessage(error));
        })
        .finally(() => {
          if (!cancelled) setJobsLoading(false);
        });
    }, query ? 300 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [jobSearch]);

  useEffect(() => {
    setAssignmentPage(1);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    getHotlineHotLeadSettings(selectedId)
      .then((settings) => {
        if (cancelled || settings.scopeUserId !== selectedId) return;
        setHotLeadsOn(settings.teamEnabled);
        setHotLeadSettings(
          Object.fromEntries(
            settings.recruiters.map((row) => [
              row.userId,
              {
                enabled: row.enabled,
                max: row.maxLeads == null ? "" : String(row.maxLeads),
                passedCount: row.passedCount,
              },
            ]),
          ),
        );
      })
      .catch((error) => {
        if (!cancelled) toast.error(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || !showSourceTools) return;
    let cancelled = false;
    getHotlineLiveSheetBranches(selectedId)
      .then((settings) => {
        if (cancelled || settings.scopeUserId !== selectedId) return;
        setLiveSheets(
          Object.fromEntries(settings.sources.map((source) => [source.source, { enabled: source.enabled }])),
        );
      })
      .catch((error) => {
        if (!cancelled) toast.error(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, showSourceTools]);

  const jobLabel = (job: HotlineJobOption) =>
    [job.title, job.companyName, job.location].filter(Boolean).join(" · ");

  const jobOptions = useMemo(
    () =>
      jobs.map((job) => {
        const label = jobLabel(job);
        return { value: job.id, label, searchText: label };
      }),
    [jobs],
  );
  const pickedJobLabel =
    pickedJob && pickedJob.id === form.jobId ? jobLabel(pickedJob) : undefined;
  const chooseJob = (jobId: string) => {
    setForm((current) => ({ ...current, jobId }));
    setPickedJob(jobs.find((job) => job.id === jobId) ?? null);
  };

  const names = useMemo(() => {
    const map = new Map(people.map((person) => [person.id, person]));
    return map;
  }, [people]);

  const visibleIds = useMemo(
    () => (selectedId ? branchIds(people, selectedId) : new Set<string>()),
    [people, selectedId],
  );

  const visibleItems = items.filter((item) => visibleIds.has(String(item.assignedToUserId)));
  const listedItems =
    statusFilter === "all"
      ? visibleItems
      : visibleItems.filter((item) => assignmentStatus(item) === statusFilter);
  const assignmentPageSize = 10;
  const assignmentPageCount = Math.max(1, Math.ceil(listedItems.length / assignmentPageSize));
  const assignmentPageSafe = Math.min(assignmentPage, assignmentPageCount);
  const pagedItems = listedItems.slice(
    (assignmentPageSafe - 1) * assignmentPageSize,
    assignmentPageSafe * assignmentPageSize,
  );
  const hierarchyQueryText = hierarchyQuery.trim();
  const roots = hierarchyPeople.filter(
    (person) => !person.managerId || !hierarchyPeople.some((other) => other.id === person.managerId),
  );
  const branchPeople = people.filter((person) => visibleIds.has(person.id));

  const hotLeadSetting = (id: string) =>
    hotLeadSettings[id] ?? { enabled: true, max: "", passedCount: 0 };

  const setHotLeadSetting = (
    id: string,
    patch: Partial<{ enabled: boolean; max: string; passedCount: number }>,
  ) => {
    setHotLeadSettings((current) => {
      const previous = current[id] ?? { enabled: true, max: "", passedCount: 0 };
      return { ...current, [id]: { ...previous, ...patch } };
    });
  };

  const toggleHotLeadTeam = async () => {
    if (!selectedId || !canEditHotLeads) return;
    const next = !hotLeadsOn;
    setHotLeadsOn(next);
    try {
      await saveHotlineHotLeadTeam({ scopeUserId: selectedId, enabled: next });
    } catch (error) {
      setHotLeadsOn(!next);
      toast.error(errorMessage(error));
    }
  };

  const toggleHotLeadRecruiter = async (id: string) => {
    if (!canEditHotLeads || !hotLeadsOn) return;
    const previous = hotLeadSetting(id);
    const next = !previous.enabled;
    setHotLeadSetting(id, { enabled: next });
    try {
      const saved = await saveHotlineHotLeadRecruiter({ userId: id, enabled: next });
      setHotLeadSetting(id, { enabled: saved.enabled, passedCount: saved.passedCount });
    } catch (error) {
      setHotLeadSetting(id, { enabled: previous.enabled });
      toast.error(errorMessage(error));
    }
  };

  const saveHotLeadMax = async (id: string) => {
    if (!canEditHotLeads || !hotLeadsOn) return;
    const raw = hotLeadSetting(id).max.trim();
    const maxLeads = raw === "" ? null : Number(raw);
    if (maxLeads != null && (!Number.isInteger(maxLeads) || maxLeads < 0 || maxLeads > 100)) {
      toast.error("Max leads must be a whole number from 0 to 100.");
      return;
    }
    try {
      const saved = await saveHotlineHotLeadRecruiter({ userId: id, maxLeads });
      setHotLeadSetting(id, {
        max: saved.maxLeads == null ? "" : String(saved.maxLeads),
        passedCount: saved.passedCount,
        enabled: saved.enabled,
      });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const liveSheetSetting = (id: string) => liveSheets[id] ?? { enabled: false };

  const setLiveSheet = (id: string, patch: Partial<{ enabled: boolean }>) => {
    setLiveSheets((current) => {
      const previous = current[id] ?? { enabled: false };
      return { ...current, [id]: { ...previous, ...patch } };
    });
  };

  const toggleLiveSheet = async (id: string) => {
    if (!selectedId) return;
    const next = liveSheetSetting(id).enabled !== true;
    setLiveSheet(id, { enabled: next });
    try {
      await saveHotlineLiveSheetBranch({ scopeUserId: selectedId, source: id, enabled: next });
    } catch (error) {
      setLiveSheet(id, { enabled: !next });
      toast.error(errorMessage(error));
    }
  };

  const removeItem = async (item: HotlineItem) => {
    if (!window.confirm(`Remove ${item.candidateName} from assigned work?`)) return;
    setRemovingId(item.id);
    try {
      await removeHotlineItem(item.id);
      setItems((current) => current.filter((row) => row.id !== item.id));
      toast.success("Removed from assigned work.");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRemovingId("");
    }
  };

  const downloadSampleCsv = () => {
    const blob = new Blob(["Candidate name,Mobile\nAsha Menon,9845012345\n"], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "hotline-queue-sample.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const uploadCsv = async (file: File) => {
    const rows = parseQueueCsv(await file.text()).map((row) => {
      const phone = row.phone.replace(/\D/g, "").slice(-10);
      const reason = !row.candidateName.trim()
        ? "Missing name"
        : phone.length !== 10
          ? "Mobile must be 10 digits"
          : "";
      return {
        candidateName: row.candidateName.trim(),
        phone: row.phone.trim(),
        ready: !reason,
        reason,
      };
    });
    if (!rows.length) {
      toast.error("The CSV has no rows. Use columns Candidate name and Mobile.");
      return;
    }
    setCsvFileName(file.name);
    setCsvRows(rows);
    setForm((current) => ({ ...current, jobId: "" }));
  };

  const assignCsv = async () => {
    if (!selectedId) {
      toast.error("Choose someone in the hierarchy first.");
      return;
    }
    const job = jobs.find((row) => row.id === form.jobId);
    if (!job) {
      toast.error("Choose a job, then assign the CSV.");
      return;
    }
    const rows = csvRows.filter((row) => row.ready);
    if (!rows.length) {
      toast.error("None of the CSV rows can be assigned.");
      return;
    }
    setUploading(true);
    try {
      const result = await importHotlineAssignments({
        assignedToUserId: selectedId,
        jobTitle: job.title,
        companyName: job.companyName || undefined,
        rows,
      });
      setItems((current) => [...result.items.slice().reverse(), ...current]);
      setAssignmentPage(1);
      setCsvRows([]);
      setCsvFileName("");
      const skipped = result.skipped.length;
      toast.success(
        skipped
          ? `Added ${result.items.length} to Assigned work. ${skipped} row${skipped === 1 ? "" : "s"} skipped.`
          : `Added ${result.items.length} to Assigned work.`,
      );
      document.getElementById("hotline-assigned-work")?.scrollIntoView({ behavior: "smooth" });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Hotline</h1>
        <p className="mt-1 text-sm text-slate-500">
          Queues are filled from Excel, Hot leads, Apna, Jobhe, and Work India. An admin sees every branch. Everyone else sees only the people under them.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setPanel("assign")}
          className={`rounded-lg px-4 py-2 text-sm font-medium ${
            panel === "assign" ? "bg-[#EC6E51] text-white" : "border border-slate-200 bg-white text-slate-700"
          }`}
        >
          Assignments
        </button>
        {showTracking && (
          <button
            type="button"
            onClick={() => setPanel("live")}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              panel === "live" ? "bg-[#EC6E51] text-white" : "border border-slate-200 bg-white text-slate-700"
            }`}
          >
            Live tracking
          </button>
        )}
        {showTracking && (
          <button
            type="button"
            onClick={() => setPanel("track")}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              panel === "track" ? "bg-[#EC6E51] text-white" : "border border-slate-200 bg-white text-slate-700"
            }`}
          >
            Call status
          </button>
        )}
        {showTracking && (
          <button
            type="button"
            onClick={() => setPanel("analytics")}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              panel === "analytics" ? "bg-[#EC6E51] text-white" : "border border-slate-200 bg-white text-slate-700"
            }`}
          >
            Analytics
          </button>
        )}
      </div>

      {panel === "live" && showTracking ? (
        <HotlineLiveTracking />
      ) : panel === "track" && showTracking ? (
        <HotlineTracking />
      ) : panel === "analytics" && showTracking ? (
        <HotlineAnalytics />
      ) : (
      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-800">Hierarchy</h2>
          <p className="mb-3 mt-1 text-xs text-slate-500">
            Select a person to assign work to them and everyone under them.
          </p>
          <input
            type="search"
            value={hierarchyQuery}
            onChange={(event) => setHierarchyQuery(event.target.value)}
            placeholder="Search the rest of the team"
            className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
          />
          {loading ? (
            <p className="text-sm text-slate-500">Loading team…</p>
          ) : hierarchyTotal === 0 ? (
            <p className="text-sm text-slate-500">No one is in this branch yet.</p>
          ) : hierarchyQueryText ? (
            hierarchyPeople.length === 0 ? (
              <p className="text-sm text-slate-500">No one else matches that search.</p>
            ) : (
              <ul className="space-y-1">
                {hierarchyPeople.map((person) => {
                  const selected = person.id === selectedId;
                  return (
                    <li key={person.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(person.id)}
                        className={`w-full rounded-lg px-2 py-2 text-left ${
                          selected ? "bg-[#FFF1EC] text-[#C2412A]" : "hover:bg-slate-50"
                        }`}
                      >
                        <div className="text-sm font-medium">{person.username}</div>
                        <div className="text-xs text-slate-500">{ROLE_LABEL[person.role] || person.role}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            <>
              <ul className="space-y-1">
                {roots.map((person) => (
                  <TeamNode
                    key={person.id}
                    person={person}
                    people={hierarchyPeople}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                  />
                ))}
              </ul>
              {hierarchyTotal > HIERARCHY_PREVIEW ? (
                <p className="mt-3 text-xs text-slate-500">
                  Showing {HIERARCHY_PREVIEW} of {hierarchyTotal}. Search to find the rest.
                </p>
              ) : null}
            </>
          )}
        </section>

        <div className="space-y-6">
          {showExcel && (
          <form
            onSubmit={(event) => event.preventDefault()}
            className="rounded-xl border border-slate-200 bg-white p-4"
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-[#C2412A]">Source 1</p>
                <h2 className="text-sm font-semibold text-slate-800">Excel</h2>
                <p className="mt-1 text-xs text-slate-500">
                  Managers add names from a sheet. This goes to{" "}
                  <span className="font-medium text-slate-700">
                    {names.get(selectedId)?.username || "the selected person"}
                  </span>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={uploading || !selectedId}
                onClick={() => csvInputRef.current?.click()}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 disabled:opacity-60"
              >
                {uploading ? "Uploading…" : "Upload CSV"}
              </button>
              <button
                type="button"
                onClick={downloadSampleCsv}
                className="text-sm font-medium text-[#C2412A]"
              >
                Sample CSV
              </button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) uploadCsv(file);
                }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Upload a CSV, review the rows, then assign them. Columns: Candidate name, Mobile.
            </p>
            {csvRows.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-sm font-medium text-slate-800">
                    {csvFileName} · {csvRows.filter((row) => row.ready).length} ready
                    {csvRows.some((row) => !row.ready)
                      ? ` · ${csvRows.filter((row) => !row.ready).length} skipped`
                      : ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setCsvRows([]);
                      setCsvFileName("");
                    }}
                    className="text-sm text-slate-500"
                  >
                    Clear
                  </button>
                </div>
                <div className="border-b border-slate-200 px-3 py-3">
                  <Field label="Job">
                    <SearchableSelect
                      options={jobOptions}
                      value={form.jobId}
                      onValueChange={chooseJob}
                      onSearchChange={setJobSearch}
                      selectedLabel={pickedJobLabel}
                      loading={jobsLoading}
                      placeholder={jobsTotal === 0 ? "No jobs available" : "Select a job for these rows"}
                      searchPlaceholder="Search the rest of the jobs"
                      emptyText={
                        jobSearch.trim()
                          ? "No other jobs match that search."
                          : "No jobs available"
                      }
                      disabled={jobsTotal === 0}
                      className="h-auto min-h-[38px] rounded-lg border-slate-200 bg-white px-3 py-2 text-sm font-normal text-slate-900 shadow-none hover:bg-white"
                    />
                  </Field>
                  <button
                    type="button"
                    disabled={uploading || !selectedId || !form.jobId}
                    onClick={assignCsv}
                    className="mt-3 rounded-lg bg-[#EC6E51] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                  >
                    {uploading ? "Assigning…" : "Assign CSV"}
                  </button>
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Candidate</th>
                        <th className="px-3 py-2">Mobile</th>
                        <th className="px-3 py-2">Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.map((row, index) => (
                        <tr key={`${row.phone}-${index}`} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-900">{row.candidateName || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{row.phone || "—"}</td>
                          <td className={`px-3 py-2 ${row.ready ? "text-emerald-700" : "text-red-600"}`}>
                            {row.ready ? "Ready" : row.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </form>
          )}
          {showSourceTools && (
          <>
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-[#C2412A]">Source 2</p>
                <h2 className="text-sm font-semibold text-slate-800">Hot leads</h2>
                <p className="mt-1 max-w-xl text-xs text-slate-500">
                  This switch is for the team you have selected. A lead goes to one recruiter who is included by the current Hot Leads rules, then the next lead goes to the next recruiter. Max leads is how many that person can receive. Turning the team or a person off stops new leads.
                </p>
              </div>
              <SwitchControl
                checked={hotLeadsOn}
                disabled={!selectedId || !canEditHotLeads}
                label={hotLeadsOn ? "On" : "Off"}
                onClick={() => void toggleHotLeadTeam()}
              />
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2">Recruiter</th>
                    <th className="px-2 py-2">Hot leads</th>
                    <th className="px-2 py-2">Max leads</th>
                  </tr>
                </thead>
                <tbody>
                  {branchPeople.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-2 py-6 text-center text-slate-500">
                        {loading ? "Loading team…" : "Select someone in the hierarchy."}
                      </td>
                    </tr>
                  ) : (
                    branchPeople.map((person) => {
                      const setting = hotLeadSetting(person.id);
                      return (
                        <tr key={person.id} className="border-t border-slate-100">
                          <td className="px-2 py-3">
                            <div className="font-medium text-slate-900">{person.username}</div>
                            <div className="text-xs text-slate-500">
                              {ROLE_LABEL[person.role] || person.role}
                            </div>
                          </td>
                          <td className="px-2 py-3">
                            <SwitchControl
                              checked={hotLeadsOn && setting.enabled}
                              disabled={!hotLeadsOn || !canEditHotLeads}
                              onClick={() => void toggleHotLeadRecruiter(person.id)}
                            />
                          </td>
                          <td className="px-2 py-3">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              inputMode="numeric"
                              disabled={!hotLeadsOn || !setting.enabled || !canEditHotLeads}
                              value={setting.max}
                              placeholder="20"
                              onChange={(event) =>
                                setHotLeadSetting(person.id, { max: event.target.value })
                              }
                              onBlur={() => void saveHotLeadMax(person.id)}
                              className={`${inputClass} max-w-[120px] disabled:bg-slate-50`}
                            />
                            {setting.passedCount > 0 && (
                              <div className="mt-1 text-xs text-slate-500">
                                {setting.passedCount} passed
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-[#C2412A]">Sources 3–5</p>
            <h2 className="text-sm font-semibold text-slate-800">Live sheets</h2>
            <p className="mt-1 max-w-xl text-xs text-slate-500">
              An admin uploads Apna, Jobhe, and Work India and keeps each sheet live. Managers do not upload these. Turn a sheet on to pass new rows to the recruiter assigned to that job.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2">Sheet</th>
                    <th className="px-2 py-2">Uploaded by</th>
                    <th className="px-2 py-2">Pass to this branch</th>
                  </tr>
                </thead>
                <tbody>
                  {SHEET_SOURCES.map((source) => {
                    const setting = liveSheetSetting(source.id);
                    return (
                      <tr key={source.id} className="border-t border-slate-100">
                        <td className="px-2 py-3">
                          <div className="font-medium text-slate-900">{source.title}</div>
                          <div className="text-xs text-slate-500">Source {source.number} · Live</div>
                        </td>
                        <td className="px-2 py-3 text-slate-600">Admin</td>
                        <td className="px-2 py-3">
                          <SwitchControl
                            checked={setting.enabled}
                            disabled={!selectedId}
                            label={setting.enabled ? "On" : "Off"}
                            onClick={() => void toggleLiveSheet(source.id)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              {branchPeople.length === 0
                ? "Select someone in the hierarchy."
                : `Applies to ${branchPeople.length} ${branchPeople.length === 1 ? "person" : "people"} in this branch.`}
            </p>
          </section>
          </>
          )}

          <section id="hotline-assigned-work" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-800">
                Assigned work ({listedItems.length})
              </h2>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                  Status
                  <SearchableSelect
                    options={STATUS_FILTER_OPTIONS}
                    value={statusFilter}
                    searchable={false}
                    onValueChange={(next) => {
                      setStatusFilter(next);
                      setAssignmentPage(1);
                    }}
                    placeholder="All"
                    className="h-8 w-52 rounded-lg border-slate-200 bg-white px-2.5 text-sm font-normal normal-case tracking-normal text-slate-800 shadow-none hover:bg-white"
                  />
                </label>
                {listedItems.length > 0 && (
                  <>
                    <button
                      type="button"
                      disabled={assignmentPageSafe <= 1}
                      onClick={() => setAssignmentPage(assignmentPageSafe - 1)}
                      className="rounded-lg border border-slate-200 px-2.5 py-1 font-medium text-slate-800 disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <span>
                      {assignmentPageSafe} / {assignmentPageCount}
                    </span>
                    <button
                      type="button"
                      disabled={assignmentPageSafe >= assignmentPageCount}
                      onClick={() => setAssignmentPage(assignmentPageSafe + 1)}
                      className="rounded-lg border border-slate-200 px-2.5 py-1 font-medium text-slate-800 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Candidate</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Assigned to</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {listedItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        {loading
                          ? "Loading assignments…"
                          : visibleItems.length === 0
                            ? "Nothing is assigned in this branch yet."
                            : "No assignments with that status."}
                      </td>
                    </tr>
                  ) : (
                    pagedItems.map((item) => {
                      const owner = names.get(String(item.assignedToUserId));
                      return (
                        <tr key={item.id} className="border-t border-slate-100">
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{item.candidateName}</div>
                            <div className="text-xs text-slate-500">{item.phone}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                              {sourceLabel(item.category, item.sourceType)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {item.jobTitle || "Open role"}
                            {item.companyName ? ` · ${item.companyName}` : ""}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-800">{owner?.username || "Unknown"}</div>
                            <div className="text-xs text-slate-500">
                              {ROLE_LABEL[owner?.role || ""] || owner?.role || ""}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {assignmentStatus(item)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              disabled={removingId === item.id}
                              onClick={() => removeItem(item)}
                              className="text-sm font-medium text-[#C2412A] disabled:opacity-60"
                            >
                              {removingId === item.id ? "Removing…" : "Remove"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
      )}
    </div>
  );
};

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#EC6E51]";

function SwitchControl({
  checked,
  disabled,
  onClick,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className="flex items-center gap-2 text-sm font-medium text-slate-800 disabled:opacity-40"
    >
      {label ? <span>{label}</span> : null}
      <span className={`relative h-6 w-11 rounded-full ${checked ? "bg-[#EC6E51]" : "bg-slate-300"}`}>
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white ${checked ? "left-5" : "left-0.5"}`}
        />
      </span>
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function TeamNode({
  person,
  people,
  selectedId,
  onSelect,
}: {
  person: HotlinePerson;
  people: HotlinePerson[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const children = people.filter((other) => other.managerId === person.id);
  const selected = person.id === selectedId;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(person.id)}
        className={`w-full rounded-lg px-2 py-2 text-left ${
          selected ? "bg-[#FFF1EC] text-[#C2412A]" : "hover:bg-slate-50"
        }`}
      >
        <div className="text-sm font-medium">{person.username}</div>
        <div className="text-xs text-slate-500">{ROLE_LABEL[person.role] || person.role}</div>
      </button>
      {children.length > 0 && (
        <ul className="ml-3 mt-1 space-y-1 border-l border-slate-200 pl-2">
          {children.map((child) => (
            <TeamNode
              key={child.id}
              person={child}
              people={people}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default HotlinePage;
